import { NICHE_PATH_LIST } from "../constants/niches";

export const SITE_NAME = "EzePulse";

const SITE_URL_ENV = (process.env.REACT_APP_SITE_URL || "").replace(/\/$/, "");

export const PAGE_META_DEFAULTS = {
    title: `${SITE_NAME} | App`,
    description: "Create campaigns and AI-powered content with EzePulse.",
};

export const HOME_PAGE_SEO = {
    title: "AI Social Media Campaign Planner & AI Marketing Trends",
    description:
        "Plan social media campaigns, discover important marketing trends your business should post about, and create AI-powered posts for Facebook and Instagram with EzePulse. Free to explore — sign up to publish and grow your brand.",
    keywords:
        "AI social media campaigns, AI marketing trends, what's trending, social media planner, AI post generator, Facebook Instagram marketing, industry trends, EzePulse, campaign management",
    path: "/",
    imagePath: "/og-image.png",
    imageAlt: "EzePulse — AI social media campaigns and marketing trends",
};

/** @deprecated Use HOME_PAGE_SEO — kept for HomePage.jsx compatibility */
export const LANDING_PAGE_SEO = HOME_PAGE_SEO;

/** Public routes that should appear in sitemap.xml and be indexable */
export const PUBLIC_INDEXABLE_ROUTES = [
    { path: "/", changefreq: "weekly", priority: "1.0" },
    { path: "/content-ai", changefreq: "daily", priority: "0.9" },
    { path: "/plans", changefreq: "weekly", priority: "0.8" },
    ...NICHE_PATH_LIST.map((path) => ({
        path,
        changefreq: "weekly",
        priority: "0.85",
    })),
];

export const CONTENT_AI_PAGE_SEO = {
    title: "AI Marketing Trends — What's Trending for Your Business",
    description:
        "Important trends your business should post about. Browse AI-curated marketing trends, angles, and hooks for your industry — free to explore on EzePulse.",
    keywords:
        "AI marketing trends, what's trending, industry trends, social media hooks, hashtag ideas, viral content angles, marketing content ideas, EzePulse",
    path: "/content-ai",
    imagePath: "/og-image.png",
    imageAlt: "EzePulse AI Marketing Trends — what's trending for your business",
};

export function buildPageTitle(pageTitle) {
    const trimmed = String(pageTitle || "").trim();
    if (!trimmed) return PAGE_META_DEFAULTS.title;
    if (trimmed.toLowerCase().includes(SITE_NAME.toLowerCase())) return trimmed;
    return `${trimmed} | ${SITE_NAME}`;
}

export function resolveMetaText(value) {
    return typeof value === "string" ? value.trim() : "";
}

export function getSiteOrigin() {
    if (SITE_URL_ENV) return SITE_URL_ENV;
    if (typeof window !== "undefined" && window.location?.origin) {
        return window.location.origin;
    }
    return "";
}

export function buildAbsoluteUrl(path = "/") {
    const origin = getSiteOrigin();
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return origin ? `${origin}${normalizedPath}` : normalizedPath;
}

const META_DESCRIPTION_MAX = 160;

export function truncateMetaDescription(text, max = META_DESCRIPTION_MAX) {
    const trimmed = String(text || "").trim();
    if (trimmed.length <= max) return trimmed;
    return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

/** Global / public opportunities only — safe for indexable home-page SEO */
export function filterPublicOpportunities(opportunities = []) {
    const globalTagged = opportunities.filter((opp) => {
        const source = String(opp.sourceType || opp.source_type || "").toLowerCase();
        return source === "global";
    });

    if (globalTagged.length > 0) return globalTagged;

    return opportunities.filter((opp) => {
        const source = String(opp.sourceType || opp.source_type || "").toLowerCase();
        return source !== "industry";
    });
}

export function extractOpportunitySeoTerms(opportunities = [], { maxTerms = 12 } = {}) {
    const terms = [];

    const addTerm = (value) => {
        const trimmed = String(value || "").trim();
        if (!trimmed || terms.includes(trimmed)) return;
        terms.push(trimmed);
    };

    for (const opp of opportunities) {
        addTerm(opp.title);
        if (opp.opportunity && opp.opportunity !== opp.title) {
            addTerm(opp.opportunity);
        }
        (opp.searchKeywords || []).forEach(addTerm);
        (opp.hashtags || []).forEach((tag) => addTerm(String(tag).replace(/^#/, "")));
        addTerm(opp.industry);
        addTerm(opp.category);
        addTerm(opp.eventName);

        if (terms.length >= maxTerms) break;
    }

    return terms.slice(0, maxTerms);
}

export function buildDynamicHomeSeo(opportunities = []) {
    const publicOpportunities = filterPublicOpportunities(opportunities);
    const trendingTerms = extractOpportunitySeoTerms(publicOpportunities);

    if (trendingTerms.length === 0) {
        return {
            metaDescription: HOME_PAGE_SEO.description,
            metaKeywords: HOME_PAGE_SEO.keywords,
            structuredData: buildHomeStructuredData(),
            trendingTerms: [],
        };
    }

    const trendingSnippet = trendingTerms.slice(0, 5).join(", ");
    const metaDescription = truncateMetaDescription(
        `${HOME_PAGE_SEO.description} Trending now: ${trendingSnippet}.`
    );

    const metaKeywords = [
        ...new Set([...HOME_PAGE_SEO.keywords.split(/,\s*/), ...trendingTerms]),
    ]
        .filter(Boolean)
        .slice(0, 25)
        .join(", ");

    return {
        metaDescription,
        metaKeywords,
        structuredData: buildHomeStructuredData(publicOpportunities),
        trendingTerms,
    };
}

export function buildHomeStructuredData(opportunities = []) {
    const siteUrl = buildAbsoluteUrl(HOME_PAGE_SEO.path);
    const logoUrl = buildAbsoluteUrl(HOME_PAGE_SEO.imagePath);
    const pageTitle = buildPageTitle(HOME_PAGE_SEO.title);
    const publicOpportunities = filterPublicOpportunities(opportunities);
    const trendingTerms = extractOpportunitySeoTerms(publicOpportunities);
    const pageDescription =
        trendingTerms.length > 0
            ? truncateMetaDescription(
                  `${HOME_PAGE_SEO.description} Trending now: ${trendingTerms.slice(0, 5).join(", ")}.`
              )
            : HOME_PAGE_SEO.description;

    const graph = [
            {
                "@type": "Organization",
                "@id": `${siteUrl}#organization`,
                name: SITE_NAME,
                url: siteUrl,
                logo: {
                    "@type": "ImageObject",
                    url: logoUrl,
                },
                description: HOME_PAGE_SEO.description,
            },
            {
                "@type": "WebSite",
                "@id": `${siteUrl}#website`,
                url: siteUrl,
                name: SITE_NAME,
                description: HOME_PAGE_SEO.description,
                publisher: {
                    "@id": `${siteUrl}#organization`,
                },
                inLanguage: "en-US",
            },
            {
                "@type": "SoftwareApplication",
                "@id": `${siteUrl}#software`,
                name: SITE_NAME,
                applicationCategory: "BusinessApplication",
                operatingSystem: "Web",
                url: siteUrl,
                description: HOME_PAGE_SEO.description,
                image: logoUrl,
                offers: {
                    "@type": "Offer",
                    price: "0",
                    priceCurrency: "USD",
                    description: "Free to explore marketing trends and campaign tools",
                },
                publisher: {
                    "@id": `${siteUrl}#organization`,
                },
            },
            {
                "@type": "WebPage",
                "@id": `${siteUrl}#webpage`,
                url: siteUrl,
                name: pageTitle,
                description: pageDescription,
                isPartOf: {
                    "@id": `${siteUrl}#website`,
                },
                about: {
                    "@id": `${siteUrl}#organization`,
                },
                primaryImageOfPage: {
                    "@type": "ImageObject",
                    url: logoUrl,
                },
                inLanguage: "en-US",
            },
    ];

    if (publicOpportunities.length > 0) {
        const contentAiUrl = buildAbsoluteUrl("/content-ai");
        graph.push({
            "@type": "ItemList",
            "@id": `${siteUrl}#trending-opportunities`,
            name: "Trending marketing topics",
            description: "Daily-updated public content angles, hooks, and hashtags",
            numberOfItems: Math.min(publicOpportunities.length, 10),
            itemListElement: publicOpportunities.slice(0, 10).map((opp, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: opp.title || opp.opportunity || `Trend ${index + 1}`,
                url: contentAiUrl,
            })),
        });
    }

    return {
        "@context": "https://schema.org",
        "@graph": graph,
    };
}

/** @deprecated Use buildHomeStructuredData */
export function buildLandingStructuredData() {
    return buildHomeStructuredData();
}

export function buildNicheLandingStructuredData(pageConfig, opportunities = []) {
    const homeUrl = buildAbsoluteUrl("/");
    const pageUrl = buildAbsoluteUrl(pageConfig.path);
    const logoUrl = buildAbsoluteUrl("/og-image.png");
    const pageTitle = buildPageTitle(pageConfig.seoTitle);
    const websiteId = `${homeUrl}#website`;
    const organizationId = `${homeUrl}#organization`;
    const publicOpportunities = filterPublicOpportunities(opportunities);

    const graph = [
        {
            "@type": "WebPage",
            "@id": `${pageUrl}#webpage`,
            url: pageUrl,
            name: pageTitle,
            description: pageConfig.description,
            isPartOf: { "@id": websiteId },
            about: { "@id": organizationId },
            primaryImageOfPage: {
                "@type": "ImageObject",
                url: logoUrl,
            },
            inLanguage: "en-US",
        },
        {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: homeUrl,
                },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: pageConfig.header,
                    item: pageUrl,
                },
            ],
        },
        {
            "@type": "WebApplication",
            "@id": `${pageUrl}#application`,
            name: `${SITE_NAME} — ${pageConfig.header}`,
            url: pageUrl,
            description: pageConfig.description,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            image: logoUrl,
            offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
                description: "Free to explore AI marketing trends and campaign tools",
            },
            provider: { "@id": organizationId },
        },
    ];

    if (publicOpportunities.length > 0) {
        graph.push({
            "@type": "ItemList",
            "@id": `${pageUrl}#trending-opportunities`,
            name: "Trending marketing topics",
            description: "Daily-updated public content angles, hooks, and hashtags",
            numberOfItems: Math.min(publicOpportunities.length, 10),
            itemListElement: publicOpportunities.slice(0, 10).map((opp, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: opp.title || opp.opportunity || `Trend ${index + 1}`,
                url: pageUrl,
            })),
        });
    }

    return {
        "@context": "https://schema.org",
        "@graph": graph,
    };
}

export function buildDynamicNicheLandingSeo(pageConfig, opportunities = []) {
    const publicOpportunities = filterPublicOpportunities(opportunities);
    const trendingTerms = extractOpportunitySeoTerms(publicOpportunities);
    const baseKeywords = pageConfig.keywords || "";

    if (trendingTerms.length === 0) {
        return {
            metaDescription: truncateMetaDescription(pageConfig.description),
            metaKeywords: baseKeywords,
            structuredData: buildNicheLandingStructuredData(pageConfig),
            trendingTerms: [],
        };
    }

    const trendingSnippet = trendingTerms.slice(0, 5).join(", ");
    const metaDescription = truncateMetaDescription(
        `${pageConfig.description} Trending now: ${trendingSnippet}.`
    );

    const metaKeywords = [
        ...new Set([...baseKeywords.split(/,\s*/), ...trendingTerms]),
    ]
        .filter(Boolean)
        .slice(0, 25)
        .join(", ");

    return {
        metaDescription,
        metaKeywords,
        structuredData: buildNicheLandingStructuredData(pageConfig, publicOpportunities),
        trendingTerms,
    };
}

export function buildContentAiStructuredData() {
    const homeUrl = buildAbsoluteUrl("/");
    const pageUrl = buildAbsoluteUrl(CONTENT_AI_PAGE_SEO.path);
    const logoUrl = buildAbsoluteUrl(CONTENT_AI_PAGE_SEO.imagePath);
    const pageTitle = buildPageTitle(CONTENT_AI_PAGE_SEO.title);
    const websiteId = `${homeUrl}#website`;
    const organizationId = `${homeUrl}#organization`;

    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "WebPage",
                "@id": `${pageUrl}#webpage`,
                url: pageUrl,
                name: pageTitle,
                description: CONTENT_AI_PAGE_SEO.description,
                isPartOf: { "@id": websiteId },
                about: { "@id": organizationId },
                primaryImageOfPage: {
                    "@type": "ImageObject",
                    url: logoUrl,
                },
                inLanguage: "en-US",
            },
            {
                "@type": "BreadcrumbList",
                "@id": `${pageUrl}#breadcrumb`,
                itemListElement: [
                    {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: homeUrl,
                    },
                    {
                        "@type": "ListItem",
                        position: 2,
                        name: "What's Trending",
                        item: pageUrl,
                    },
                ],
            },
            {
                "@type": "WebApplication",
                "@id": `${pageUrl}#application`,
                name: `${SITE_NAME} AI Marketing Trends`,
                url: pageUrl,
                description: CONTENT_AI_PAGE_SEO.description,
                applicationCategory: "BusinessApplication",
                operatingSystem: "Web",
                image: logoUrl,
                offers: {
                    "@type": "Offer",
                    price: "0",
                    priceCurrency: "USD",
                    description: "Free to browse AI marketing trends",
                },
                provider: { "@id": organizationId },
            },
        ],
    };
}
